import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { ERROR_MESSAGES } from "../../constants/enums";
import {
  IDocumentTrainingDetail,
  IDocumentTrainingListItem,
} from "./documentTraining.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { uploadFile } from "../../features/FileUpload/utils";

// Action type constant to avoid circular import with the slice
export const UPDATE_INGESTION_STATUS = "genAiKnowledgeBase/updateIngestionStatus";

type AppDispatch = any;

// ── createAsyncThunk-based thunks ─────────────────────────────────────────────

export const fetchDocumentTrainingList = createAsyncThunk<
  { data: IDocumentTrainingListItem[]; totalRecords: number },
  { status?: string } | void,
  { rejectValue: { message: string; status?: number } }
>(
  "genAiKnowledgeBase/fetchDocumentTrainingList",
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const status = params && "status" in params ? params.status : undefined;
      if (status && status !== "-1") {
        queryParams.append("status", status);
      }
      const query = queryParams.toString();
      const res = await api.get(`/discount-document/list${query ? `?${query}` : ""}`);
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message:
          err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchDocumentTrainingDetail = createAsyncThunk<
  IDocumentTrainingDetail,
  string,
  { rejectValue: { message: string; status?: number } }
>(
  "genAiKnowledgeBase/fetchDocumentTrainingDetail",
  async (documentCode, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/discount-document/detail?discount_document_code=${documentCode}`,
      );
      const { document, items } = res.data.data;
      return {
        ...document,
        items: items || [],
      };
    } catch (err: any) {
      return rejectWithValue({
        message:
          err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);


// ── Manual thunks (side-effect-heavy) ─────────────────────────────────────────

export const ingestDocumentTrainingAsync =
  (
    data: Record<string, any>,
    file: File | null,
    onCallback: (isSuccess: boolean, discountDocumentCode?: string) => void,
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        module_name: "GEN_AI",
      };
      const filePath = await uploadFile(
        file,
        "GEN_AI",
        data.file_path || "",
        asPayload,
      );
      const res = await api.post("/discount-document/ingest", {
        ...data,
        file_path: filePath,
      });
      onCallback(true, res.data.data?.discount_document_code);
      dispatch(
        showMessage({
          type: "success",
          message: "Document ingestion queued successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const pollDocumentTrainingStatusAsync =
  (
    discountDocumentCode: string,
    onStatusChange: (status: string) => void,
  ) =>
  (dispatch: AppDispatch) => {
    const checkStatus = async () => {
      try {
        const res = await api.get(
          `/discount-document/status?discount_document_code=${discountDocumentCode}`,
        );
        const status: string = res.data.data?.status ?? res.data.status;
        dispatch({ type: UPDATE_INGESTION_STATUS, payload: status });
        onStatusChange(status);

        if (status === "READY" || status === "FAILED") {
          clearInterval(intervalId);
        }
      } catch (err: any) {
        clearInterval(intervalId);
        dispatch(
          showMessage({
            type: "error",
            message:
              err.response?.data?.message ||
              "Failed to fetch ingestion status.",
            displayAs: "snackbar",
          }),
        );
      }
    };

    // Call immediately, then poll every 5 seconds
    checkStatus();
    const intervalId = setInterval(checkStatus, 5000);

    return intervalId;
  };

export const upsertDocumentTrainingAsync =
  (
    data: Record<string, any>,
    file: File | null,
    onCallback: (isSuccess: boolean) => void,
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        module_name: "GEN_AI",
      };
      const filePath = await uploadFile(
        file,
        "GEN_AI",
        data.file_path || "",
        asPayload,
      );
      await api.put("/discount-document/upsert", {
        ...data,
        file_path: filePath,
      });
      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "Document updated successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to update document.",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
